import {
  Invitation,
  InvitationTemplateEnum,
  RoleEnum,
  UpdateInvitationDto,
} from '@sr-sdks/api-sdk-axios';
import { Button, Dialog, DropdownMenuItem, Icon } from '@sr/ui-library';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { invitationService } from '../reducers/permissions/invitationSlice';
import { userService } from '../reducers/permissions/userSlice';
import { workspaceService } from '../reducers/workspaces/workspaceSlice';
import { useAppDispatch, useTypedSelector } from '../stateStore';
import {
  isLoadingState,
  isSuccessState,
  useIsFeatureEnabled,
  userIsInvitedAsWorkspaceOwner,
  userIsWorkspaceOwner,
} from '../utils/helpers/common';
import AccessLevelDropdown from './AccessLevelDropdown';
import AppTableV2 from './AppTableV2';
import { ConfirmButtons } from './ConfirmButtons';
import { InvitationStatus } from './InvitationStatus';
import OptionsDropdown from './OptionsDropdown';
import ResendInvite from './ResendInvite';
import SendInviteForm from './SendInviteForm';

interface InvitedTableProps {
  filterElement?: React.ReactNode;
  hasBottomNavigation?: boolean;
  isReadOnly?: boolean;
  isStaffView?: boolean;
  /** Called once the external open request has been handled so the trigger can be reset. */
  onInviteModalOpened?: () => void;
  /** When true, opens the "Invite Teammate" modal (e.g. triggered by an external header button). */
  shouldOpenInviteModal?: boolean;
  workspaceId?: string;
}

interface IPendingRoleChange {
  invitationId: string;
  roleKey: RoleEnum;
}

const InvitedTable = ({
  filterElement,
  hasBottomNavigation = true,
  isReadOnly = false,
  isStaffView = false,
  onInviteModalOpened,
  shouldOpenInviteModal = false,
  workspaceId,
}: InvitedTableProps) => {
  const dispatch = useAppDispatch();

  const [isInvitationModalOpen, setIsInvitationModalOpen] = useState(false);

  const [isRemoveInvitationModalOpen, setIsRemoveInvitationModalOpen] =
    useState(false);

  const [pendingRoleChange, setPendingRoleChange] =
    useState<IPendingRoleChange>();

  const [selectedInvitation, setSelectedInvitation] = useState<Invitation>();

  // Latches the external open request so the modal opens exactly once per
  // false->true transition of `shouldOpenInviteModal`, independent of whether
  // the parent memoises `onInviteModalOpened` or resets the flag.
  const hasHandledInviteModalOpen = useRef(false);

  const isLoading = useTypedSelector(invitationService.selectors.isLoading);
  const isOnboardedUsersLoading = useTypedSelector(
    userService.selectors.isUsersLoading,
  );
  const isRemoving = useTypedSelector(invitationService.selectors.isRemoving);
  const isUpdating = useTypedSelector(invitationService.selectors.isUpdating);

  const invitations = useTypedSelector(invitationService.selectors.invitations);
  const onboardedTeammates = useTypedSelector(userService.selectors.users);

  const paginationMeta = useTypedSelector(
    invitationService.selectors.paginationMeta,
  );

  const workspace = useTypedSelector(workspaceService.selectors.workspace);

  const isStaffFeatureEnabled = useIsFeatureEnabled(
    'staff.workspace.owner.send.invite',
  );

  const isFeatureEnabledForStaffView =
    (isStaffView && isStaffFeatureEnabled) || !isStaffView;

  const handleInvitationModalClose = async () => {
    setSelectedInvitation(undefined);
    setIsInvitationModalOpen(false);
    await handlePageRequest();
  };

  const handlePageRequest = useCallback(
    (page = 1) => {
      if (!workspaceId) return;

      dispatch(invitationService.findAll({ page, workspaceId }));
    },
    [dispatch, workspaceId],
  );

  const handleRemoveInvitation = async (invitation?: Invitation) => {
    if (!invitation) return;

    const removeInvitation = await dispatch(
      invitationService.remove(invitation._id),
    );

    if (invitationService.remove.fulfilled.match(removeInvitation)) {
      setIsRemoveInvitationModalOpen(false);
      await handlePageRequest(paginationMeta?.currentPage);
    }
  };

  const handleSaveClick = async () => {
    if (!pendingRoleChange) return;

    const updateInvitationDto: UpdateInvitationDto = {
      userRoles: [pendingRoleChange.roleKey],
    };

    const updateInvitation = await dispatch(
      invitationService.update({
        id: pendingRoleChange.invitationId,
        updateInvitationDto,
      }),
    );

    if (invitationService.update.fulfilled.match(updateInvitation)) {
      setPendingRoleChange(undefined);
      handlePageRequest();
    }
  };

  const hasExistingWorkspaceOwner = useMemo(() => {
    if (!isStaffView || !workspaceId) return false;

    const hasExistingWorkspaceOwnerInvitation =
      invitations?.some((invitation) =>
        userIsInvitedAsWorkspaceOwner(invitation, workspaceId),
      ) ?? false;

    const hasExistingWorkspaceOwnerUser = onboardedTeammates.some((teammate) =>
      userIsWorkspaceOwner(teammate, workspaceId),
    );

    return hasExistingWorkspaceOwnerUser || hasExistingWorkspaceOwnerInvitation;
  }, [onboardedTeammates, invitations, isStaffView, workspaceId]);

  // Checks if data is still being loaded by checking loading states of invitations and onboarded users
  const isLoadingData =
    !isSuccessState(isLoading) || !isSuccessState(isOnboardedUsersLoading);

  // Button is disabled if data is loading, user lacks permission, or workspace already has an owner
  const isDisabled =
    isLoadingData || hasExistingWorkspaceOwner || !isFeatureEnabledForStaffView;

  useEffect(() => {
    handlePageRequest();
  }, [handlePageRequest, workspaceId]);

  // Opens the "Invite Teammate" modal when triggered externally (e.g. the
  // settings page header button), then resets the trigger.
  useEffect(() => {
    if (!shouldOpenInviteModal) {
      hasHandledInviteModalOpen.current = false;

      return;
    }

    if (hasHandledInviteModalOpen.current) return;

    // Don't open against actively-loading invitation data; the header trigger
    // (unlike the inline button) has no disabled guard, so defer until the
    // table's own fetch settles, then open on the next run.
    if (isLoadingState(isLoading)) return;

    hasHandledInviteModalOpen.current = true;
    setSelectedInvitation(undefined);
    setIsInvitationModalOpen(true);
    onInviteModalOpened?.();
  }, [shouldOpenInviteModal, onInviteModalOpened, isLoading]);

  const isNewWorkspaceOwnerInvitation =
    isStaffView && !hasExistingWorkspaceOwner;

  const invitationType =
    selectedInvitation?.invitationTemplate ||
    InvitationTemplateEnum.CustomerInvitation;

  return (
    <>
      <AppTableV2
        id="invited-table"
        customEmptyTable={{
          description: 'Invite your team to help you manage your workspace.',
          icon: 'mail',
          title: 'No invitations found',
        }}
        filterElement={
          filterElement ?? (
            <div className="flex w-full flex-row gap-5">
              <h3>Invited</h3>
              <div className="ml-auto basis-3/4 text-right">
                <Button
                  disabled={isDisabled}
                  icon={<Icon icon="plus" weight="bold" />}
                  size="sm"
                  onClick={() => {
                    setSelectedInvitation(undefined);
                    setIsInvitationModalOpen(true);
                  }}
                >
                  <span className="hidden md:inline">Invite teammate</span>
                </Button>
              </div>
            </div>
          )
        }
        hasBottomNavigation={hasBottomNavigation}
        headerRowElement={
          <tr>
            <th>Email</th>
            <th className="!w-20">Access Level</th>
            <th className="!w-32">Status</th>
            <th colSpan={2}>
              <span className="sr-only">Options</span>
            </th>
          </tr>
        }
        isLoading={isLoadingState(isLoading)}
        paginationKey="invitedTablePage"
        paginationMeta={paginationMeta}
        onPageRequest={handlePageRequest}
      >
        {invitations?.map((invitation: Invitation) => {
          return (
            <tr key={invitation._id}>
              <td className="max-w-[650px] break-words">
                {invitation.emailAddress}
              </td>
              <td className="relative">
                <AccessLevelDropdown
                  isReadOnly={
                    isReadOnly ||
                    invitation.invitationTemplate !==
                      InvitationTemplateEnum.CustomerInvitation
                  }
                  selectedRoleKey={
                    (pendingRoleChange?.invitationId === invitation._id
                      ? pendingRoleChange?.roleKey
                      : invitation.userRoles[0]) as RoleEnum
                  }
                  toggleClassName="pl-0 bg-transparent w-32 hover:bg-transparent"
                  onRoleSelect={(roleKey) =>
                    setPendingRoleChange({
                      invitationId: invitation._id,
                      roleKey,
                    })
                  }
                />
              </td>
              <td>
                <InvitationStatus
                  expires={invitation.expires}
                  invitationStatus={invitation.invitationStatus}
                />
              </td>
              <td className="!w-56">
                <ResendInvite
                  invite={invitation}
                  isReadOnly={isReadOnly}
                  onResendInviteClick={() => {
                    setSelectedInvitation(invitation);
                    setIsInvitationModalOpen(true);
                  }}
                />
              </td>
              <td className="relative !w-10 !text-center">
                {!isReadOnly && (
                  <OptionsDropdown>
                    <DropdownMenuItem
                      data-testid="remove-teammate-button"
                      onClick={() => {
                        setSelectedInvitation(invitation);
                        setIsRemoveInvitationModalOpen(true);
                      }}
                    >
                      Remove Teammate
                    </DropdownMenuItem>
                  </OptionsDropdown>
                )}
              </td>
            </tr>
          );
        })}
      </AppTableV2>

      {pendingRoleChange && (
        <ConfirmButtons
          cancelButtonProps={{
            onClick: () => setPendingRoleChange(undefined),
          }}
          className="mt-10 w-full"
          hasCancelButton
          isLoading={isLoadingState(isUpdating)}
          saveButtonProps={{
            onClick: handleSaveClick,
          }}
        />
      )}

      {isInvitationModalOpen && (
        <Dialog
          heading={selectedInvitation ? 'Resend Invitation' : 'Invite Teammate'}
          className="max-w-screen-md"
          hasActionButton={false}
          hasCancelButton={false}
          open={isInvitationModalOpen}
          onOpenChange={handleInvitationModalClose}
        >
          <SendInviteForm
            accessLevelDefaultValue={
              isNewWorkspaceOwnerInvitation
                ? RoleEnum.WorkspaceOwner
                : undefined
            }
            invitation={selectedInvitation}
            invitationType={invitationType}
            isPermittedToChangeWorkspaceOwner={isNewWorkspaceOwnerInvitation}
            isReadOnly={isNewWorkspaceOwnerInvitation}
            workspace={workspace}
            onInviteSuccess={handleInvitationModalClose}
          />
        </Dialog>
      )}

      <Dialog
        heading="Are you sure?"
        actionButtonProps={{
          children: 'Remove',
          loading: isLoadingState(isRemoving),
          onClick: () => handleRemoveInvitation(selectedInvitation),
          // Why not onClick={handleRemoveClick(selectedStaff)}?
          // The issue is that handleRemoveClick returns a Promise, but the onClick handler expects a synchronous function that returns void.
        }}
        cancelButtonProps={{
          onClick: () => setIsRemoveInvitationModalOpen(false),
        }}
        open={isRemoveInvitationModalOpen}
        onOpenChange={() => setIsRemoveInvitationModalOpen(false)}
      >
        <div>
          Are you sure you would like to remove &quot;
          <span className="font-bold">{selectedInvitation?.emailAddress}</span>
          &quot;?
        </div>
      </Dialog>
    </>
  );
};

export default InvitedTable;
